import { from_number_to_code } from './custom_predecessor';
import {
  createCustomLinkFormatterCommon,
  createDurationFormatter,
  renderLinks
} from './linkFormatterCommon';

/**
 * Fallback renderer for the Predecessors column.
 *
 * @param {Object} activity - DHTMLX Gantt task object.
 * @returns {string} The raw predecessor string or empty if none.
 */
export function fallbackRenderer(activity) {
  return activity.custom_predecessors || '';
}

/**
 * Create a custom LinkFormatter wrapping the DHTMLX PRO linkFormatter.
 *
 * Uses correlative IDs and normalizes lag units to 'd'.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} durationFormatter - The DurationFormatter to use for lag formatting.
 * @returns {Object} An object with `format(link): string` and `parse(text): Object` methods.
 * @see {@link https://docs.dhtmlx.com/gantt/desktop__formatters_ext.html#linkformatter}
 */
export function createCustomLinkFormatter(gantt, durationFormatter) {
  return createCustomLinkFormatterCommon(
    gantt,
    durationFormatter,
    'source',
    from_number_to_code
  );
}

/**
 * Render the predecessors for a Gantt task.
 *
 * Formats each link in `activity.$target` using the given formatter.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} formatter - LinkFormatter with a `format(link)` method.
 * @param {Object} activity - Gantt task object.
 * @returns {string} Semicolon-separated predecessor strings.
 */
export function renderPredecessors(gantt, formatter, activity) {
  return renderLinks(gantt, formatter, activity, '$target');
}

/**
 * Format predecessors for inline editor using a fresh formatter.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} activity - Gantt task object.
 * @returns {string} Formatted predecessor string for editing.
 */
export function formatTaskPredecessors(gantt, activity) {
  const durationFormatter = createDurationFormatter(gantt);
  const linkFormatter =
    createCustomLinkFormatter(gantt, durationFormatter) || {};
  return renderPredecessors(gantt, linkFormatter, activity);
}
